import React from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Eye, X, Trash2, Plus, Search, StickyNote, Hammer, Edit2, CheckCircle, UserCheck, CalendarPlus } from "lucide-react";
import { TICKET_STATUS_RO, TICKET_PRIORITY_RO, TICKET_CATEGORY_RO } from "../../lib/statusLabels";
import { useAuth } from "../../contexts/AuthContext";

// ── PartsPicker — search products and build a parts list with location ────────
function PartsPicker({ parts, onChange }) {
    const [q, setQ] = React.useState("");
    const [results, setResults] = React.useState([]);
    const [searching, setSearching] = React.useState(false);
    const [locationNames, setLocationNames] = React.useState({});  // {location_id: name}
    const timerRef = React.useRef(null);

    React.useEffect(() => {
        api.get("/admin/stock-locations").then(r => {
            const map = {};
            (r.data || []).forEach(l => { map[l.location_id] = l.name; });
            setLocationNames(map);
        }).catch(() => {});
    }, []);

    React.useEffect(() => {
        clearTimeout(timerRef.current);
        if (!q.trim()) { setResults([]); return; }
        timerRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const r = await api.get(`/admin/stock-locations/products-search?q=${encodeURIComponent(q)}`);
                setResults(r.data || []);
            } catch { setResults([]); }
            setSearching(false);
        }, 300);
    }, [q]);

    const addPart = (product) => {
        setQ(""); setResults([]);
        setQ("");
        onChange(prev => [...prev, {
            product_id:   product.product_id,
            product_name: product.name,
            product_code: product.code || "",
            qty:          1,
            location_id:  Object.keys(product.stock_locations || {})[0] || "",
            _stock:       product.stock,
            _locations:   product.stock_locations || {},
        }]);
    };

    const updatePart = (idx, patch) => onChange(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
    const removePart = (idx) => onChange(prev => prev.filter((_, i) => i !== idx));

    const locOptions = (part) => Object.entries(part._locations || {}).map(([lid, qty]) => ({ lid, qty }));

    return (
        <div className="space-y-2">
            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground">Piese utilizate</label>

            {/* Parts list */}
            {parts.length > 0 && (
                <div className="space-y-1">
                    {parts.map((part, idx) => {
                        const opts = locOptions(part);
                        const locQty = part._locations?.[part.location_id] ?? part._stock;
                        return (
                            <div key={idx} className="flex items-center gap-2 bg-muted/40 rounded px-2 py-1">
                                <span className="flex-1 text-sm font-medium truncate" title={part.product_name}>
                                    {part.product_name}
                                    {part.product_code && <span className="text-muted-foreground ml-1 text-xs">#{part.product_code}</span>}
                                </span>
                                {/* Location selector */}
                                <select
                                    value={part.location_id}
                                    onChange={(e) => updatePart(idx, { location_id: e.target.value })}
                                    className="input-soft !py-0.5 text-xs max-w-[130px]"
                                    title="Locație stoc"
                                >
                                    <option value="">— Fără locație —</option>
                                    {opts.map(({ lid, qty }) => (
                                        <option key={lid} value={lid}>{locationNames[lid] || lid} ({qty} buc)</option>
                                    ))}
                                </select>
                                {/* Qty input */}
                                <input
                                    type="number" min="1"
                                    max={part.location_id ? (part._locations?.[part.location_id] ?? undefined) : undefined}
                                    value={part.qty}
                                    onChange={(e) => updatePart(idx, { qty: Math.max(1, Number(e.target.value)) })}
                                    className="input-soft !py-0.5 text-xs w-16 text-center"
                                />
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    / {part.location_id ? (part._locations?.[part.location_id] ?? "?") : part._stock} buc
                                </span>
                                <button onClick={() => removePart(idx)} className="text-destructive hover:opacity-70">
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Search input */}
            <div className="relative">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={searching ? "Se caută..." : "Caută produs după nume sau cod..."}
                    className="input-soft text-sm w-full"
                />
                {results.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded shadow-lg max-h-48 overflow-y-auto">
                        {results.map(prod => {
                            const locCount = Object.keys(prod.stock_locations || {}).length;
                            return (
                                <button
                                    key={prod.product_id}
                                    onClick={() => addPart(prod)}
                                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between gap-2"
                                >
                                    <span className="truncate">
                                        {prod.name}
                                        {prod.code && <span className="text-muted-foreground ml-1 text-xs">#{prod.code}</span>}
                                    </span>
                                    <span className="text-xs text-muted-foreground shrink-0">
                                        {prod.stock} buc{locCount > 0 ? ` · ${locCount} loc.` : ""}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}


const emptyTicket = () => ({
    subject: "", name: "", phone: "", email: "", address: "", city: "",
    category: "general", priority: "normal", description: "", source: "offline", status: "open",
    user_id: null, crm_contact_id: null, order_id: null, add_to_crm: true,
});

export default function AdminTickets() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "superadmin";
    const [tickets, setTickets] = React.useState([]);
    const [q, setQ] = React.useState("");
    const [statusFilter, setStatusFilter] = React.useState("");
    const [viewing, setViewing] = React.useState(null);
    const [creating, setCreating] = React.useState(null);
    const [noteDraft, setNoteDraft] = React.useState("");
    const [editingNote, setEditingNote] = React.useState(null);
    const [ivForm, setIvForm] = React.useState({ date: new Date().toISOString().slice(0, 10), duration_minutes: 0, work_done: "", cost: 0 });
    const [ivParts, setIvParts] = React.useState([]);  // structured parts list
    const [clientSuggest, setClientSuggest] = React.useState({ registered: [], offline: [] });
    const [resolution, setResolution] = React.useState("");
    const [sendResEmail, setSendResEmail] = React.useState(false);
    const [installers, setInstallers] = React.useState([]);
    const [acceptForm, setAcceptForm] = React.useState(null);

    React.useEffect(() => {
        api.get("/admin/users").then(r => setInstallers((r.data || []).filter(u =>
            u.role === "installer" || (u.roles || []).includes("installer")))).catch(() => {});
    }, []);

    const [sp] = useSearchParams();
    React.useEffect(() => {
        const openId = sp.get("id") || sp.get("open");
        if (openId) openT(openId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sp]);

    const load = React.useCallback(async () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (statusFilter) params.set("status", statusFilter);
        try {
            const r = await api.get("/admin/tickets/search?" + params.toString());
            setTickets(r.data || []);
        } catch (e) {
            // Silent fail (e.g. 403 for restricted role) — installer-allowed endpoint scopes results
            setTickets([]);
        }
    }, [q, statusFilter]);

    React.useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
    }, [load]);

    const openT = async (id) => {
        const r = await api.get(`/admin/tickets/${id}`);
        setViewing(r.data);
        setResolution(r.data?.resolution || "");
        setSendResEmail(false);
    };
    const refreshView = async () => {
        if (!viewing) return;
        const r = await api.get(`/admin/tickets/${viewing.ticket_id}`); setViewing(r.data); load();
    };
    const updateTicket = async (patch) => {
        await api.put(`/admin/tickets/${viewing.ticket_id}`, patch); refreshView();
    };
    const resolveTicket = async () => {
        if (!resolution.trim()) { toast.error("Scrie rezoluția mai întâi"); return; }
        if (sendResEmail && !viewing.email) {
            if (!window.confirm("Ticket-ul nu are email — nu se poate trimite. Continui fără email?")) return;
        }
        const r = await api.post(`/admin/tickets/${viewing.ticket_id}/resolve`,
            { resolution, send_email: sendResEmail });
        toast.success(r.data?.email_sent ? "Marcat rezolvat + email trimis" : "Marcat rezolvat");
        refreshView();
    };
    const assignInstaller = async (installer_id) => {
        await api.put(`/admin/tickets/${viewing.ticket_id}/assign`, { installer_id: installer_id || null });
        toast.success("Atribuit"); refreshView();
    };
    const acceptRequest = async () => {
        const payload = acceptForm || {};
        const r = await api.post(`/admin/tickets/${viewing.ticket_id}/accept-service-request`, payload);
        setAcceptForm(null);
        toast.success(r.data?.already_existed ? "Programare existentă" : "Programare creată! Vezi în /admin/programari");
        refreshView();
    };
    const del = async (id) => {
        if (!window.confirm("Șterge definitiv?")) return;
        await api.delete(`/admin/tickets/${id}`); setViewing(null); load();
    };

    // Internal notes
    const addNote = async () => {
        if (!noteDraft.trim()) return;
        await api.post(`/admin/tickets/${viewing.ticket_id}/notes`, { text: noteDraft });
        setNoteDraft(""); refreshView();
    };
    const saveEditNote = async () => {
        await api.put(`/admin/tickets/${viewing.ticket_id}/notes/${editingNote.note_id}`, { text: editingNote.text });
        setEditingNote(null); refreshView();
    };
    const deleteNote = async (nid) => {
        if (!window.confirm("Șterge nota?")) return;
        await api.delete(`/admin/tickets/${viewing.ticket_id}/notes/${nid}`); refreshView();
    };

    // Interventions
    const addIntv = async () => {
        if (!ivForm.work_done.trim()) return toast.error("Descrie intervenția");
        await api.post(`/admin/tickets/${viewing.ticket_id}/interventions`, { ...ivForm, parts: ivParts });
        setIvForm({ date: new Date().toISOString().slice(0, 10), duration_minutes: 0, work_done: "", cost: 0 });
        setIvParts([]);
        refreshView(); toast.success("Intervenție adăugată");
    };
    const deleteIntv = async (iid) => {
        if (!window.confirm("Ștergi intervenția?")) return;
        await api.delete(`/admin/tickets/${viewing.ticket_id}/interventions/${iid}`); refreshView();
    };

    // Manual create + client autocomplete
    const searchClient = async (value) => {
        setCreating(c => ({ ...c, name: value }));
        if (value.length < 2) { setClientSuggest({ registered: [], offline: [], crm: [], orders: [] }); return; }
        try {
            const r = await api.get(`/admin/clients/search?q=${encodeURIComponent(value)}`);
            setClientSuggest(r.data || { registered: [], offline: [], crm: [], orders: [] });
        } catch {}
    };
    const pickClient = (c, kind) => {
        setCreating(cc => ({
            ...cc,
            name: c.name || "", phone: c.phone || "", email: c.email || "",
            address: c.address || "", city: c.city || "",
            user_id: c.user_id || null,
            crm_contact_id: c.contact_id || null,
            order_id: c.order_id || null,
            source: kind === "registered" ? "site" : kind === "crm" ? "crm" : kind === "order" ? "site" : "offline",
            add_to_crm: kind !== "crm" && kind !== "registered",
        }));
        setClientSuggest({ registered: [], offline: [], crm: [], orders: [] });
    };
    const submitCreate = async () => {
        if (!creating.subject.trim()) return toast.error("Subiect obligatoriu");
        await api.post("/admin/tickets", creating);
        toast.success("Ticket creat"); setCreating(null); load();
    };

    const totalSuggest = (clientSuggest.registered?.length || 0) + (clientSuggest.offline?.length || 0) +
                         (clientSuggest.crm?.length || 0) + (clientSuggest.orders?.length || 0);

    return (
        <div data-testid="admin-tickets">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl">Tickete service</h1>
                </div>
                <button onClick={() => setCreating(emptyTicket())} className="btn-primary flex items-center gap-2" data-testid="ticket-manual-create"><Plus className="h-4 w-4" /> Ticket manual</button>
            </div>

            <div className="card-soft p-3 mb-4 flex flex-wrap gap-2 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută după nume, telefon, email, număr ticket..."
                           className="input-soft !py-2 flex-1" data-testid="tickets-search" />
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-soft !py-2" data-testid="tickets-status-filter">
                    <option value="">Toate statusurile</option>
                    {Object.entries(TICKET_STATUS_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <span className="text-xs text-muted-foreground">{tickets.length} tickete</span>
            </div>

            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted"><tr>{["Nr.", "Dată", "Client", "Subiect", "Categorie", "Prior.", "Status", ""].map(h => <th key={h} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">{h}</th>)}</tr></thead>
                    <tbody>
                        {tickets.length === 0 && <tr><td colSpan="8" className="px-3 py-8 text-center text-muted-foreground">Niciun ticket.</td></tr>}
                        {tickets.map((t, i) => (
                            <tr key={t.ticket_id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => openT(t.ticket_id)} data-testid={`ticket-row-${i}`}>
                                <td className="px-3 py-2 font-mono text-xs">#{t.ticket_number}</td>
                                <td className="px-3 py-2 text-xs">{new Date(t.created_at).toLocaleDateString("ro-RO")}</td>
                                <td className="px-3 py-2"><div className="font-medium">{t.name || "—"}</div><div className="text-xs text-muted-foreground">{t.phone || t.email || "—"}</div></td>
                                <td className="px-3 py-2">{t.subject}</td>
                                <td className="px-3 py-2 text-xs">{TICKET_CATEGORY_RO[t.category] || t.category}</td>
                                <td className="px-3 py-2 text-xs">{TICKET_PRIORITY_RO[t.priority] || t.priority}</td>
                                <td className="px-3 py-2"><span className={`chip text-[10px] ${["resolved", "closed"].includes(t.status) ? "chip-success" : "chip-primary"}`}>{TICKET_STATUS_RO[t.status] || t.status}</span></td>
                                <td className="px-3 py-2 text-right"><Eye className="h-4 w-4 inline" /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create ticket modal */}
            {creating && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <h3 className="font-heading text-xl">Ticket nou (manual)</h3>
                            <button onClick={() => setCreating(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="relative">
                                <label className="text-xs text-muted-foreground">Nume client (caută existent sau scrie unul nou)</label>
                                <input value={creating.name} onChange={(e) => searchClient(e.target.value)} placeholder="Ex: Ion Popescu"
                                       className="input-soft" data-testid="tcreate-name" autoComplete="off" />
                                {totalSuggest > 0 && (
                                    <div className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg z-10 max-h-72 overflow-y-auto" data-testid="client-suggest-list">
                                        {clientSuggest.registered?.map(c => (
                                            <button type="button" key={"u" + c.user_id} onClick={() => pickClient(c, "registered")} className="w-full text-left px-3 py-2 hover:bg-muted border-b border-border/40">
                                                <div className="font-medium text-sm">{c.name} <span className="chip text-[10px] bg-primary/10 text-primary ml-1">cont</span></div>
                                                <div className="text-xs text-muted-foreground">{c.email} · {c.phone}</div>
                                            </button>
                                        ))}
                                        {clientSuggest.crm?.map(c => (
                                            <button type="button" key={"c" + c.contact_id} onClick={() => pickClient(c, "crm")} className="w-full text-left px-3 py-2 hover:bg-muted border-b border-border/40">
                                                <div className="font-medium text-sm">{c.name} <span className="chip text-[10px] bg-amber-100 text-amber-900 ml-1">CRM</span></div>
                                                <div className="text-xs text-muted-foreground">{c.phone || "—"} {c.company && `· ${c.company}`}</div>
                                            </button>
                                        ))}
                                        {clientSuggest.offline?.map((c, i) => (
                                            <button type="button" key={"o" + i} onClick={() => pickClient(c, "offline")} className="w-full text-left px-3 py-2 hover:bg-muted border-b border-border/40">
                                                <div className="font-medium text-sm">{c.name} <span className="chip text-[10px] bg-muted ml-1">offline · {c.ticket_count} tickete</span></div>
                                                <div className="text-xs text-muted-foreground">{c.phone || "—"} · {c.city || ""}</div>
                                            </button>
                                        ))}
                                        {clientSuggest.orders?.map(o => (
                                            <button type="button" key={"or" + o.order_id} onClick={() => pickClient(o, "order")} className="w-full text-left px-3 py-2 hover:bg-muted border-b border-border/40">
                                                <div className="font-medium text-sm">{o.name} <span className="chip text-[10px] bg-blue-100 text-blue-900 ml-1">comanda #{o.order_number}</span></div>
                                                <div className="text-xs text-muted-foreground">{o.phone || o.email} · {o.total?.toFixed(2)} RON</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input value={creating.phone} onChange={(e) => setCreating(c => ({ ...c, phone: e.target.value }))} placeholder="Telefon" className="input-soft" data-testid="tcreate-phone" />
                                <input value={creating.email} onChange={(e) => setCreating(c => ({ ...c, email: e.target.value }))} placeholder="Email (opțional)" className="input-soft" data-testid="tcreate-email" />
                                <input value={creating.address} onChange={(e) => setCreating(c => ({ ...c, address: e.target.value }))} placeholder="Adresă" className="input-soft md:col-span-2" />
                                <input value={creating.city} onChange={(e) => setCreating(c => ({ ...c, city: e.target.value }))} placeholder="Oraș" className="input-soft" />
                                <select value={creating.category} onChange={(e) => setCreating(c => ({ ...c, category: e.target.value }))} className="input-soft">
                                    {Object.entries(TICKET_CATEGORY_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                </select>
                            </div>
                            <input required value={creating.subject} onChange={(e) => setCreating(c => ({ ...c, subject: e.target.value }))} placeholder="Subiect *" className="input-soft" data-testid="tcreate-subject" />
                            <textarea rows="4" value={creating.description} onChange={(e) => setCreating(c => ({ ...c, description: e.target.value }))} placeholder="Descriere problemă" className="input-soft" />
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={!!creating.add_to_crm} onChange={(e) => setCreating(c => ({ ...c, add_to_crm: e.target.checked }))} data-testid="tcreate-add-crm" />
                                Adaugă clientul în agenda CRM dacă nu există deja
                            </label>
                            {(creating.user_id || creating.crm_contact_id || creating.order_id) && (
                                <div className="text-xs text-muted-foreground">
                                    Legat de: {creating.user_id && "cont înregistrat"}{creating.crm_contact_id && "contact CRM existent"}{creating.order_id && `comanda #${creating.order_id.slice(-8)}`}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <select value={creating.priority} onChange={(e) => setCreating(c => ({ ...c, priority: e.target.value }))} className="input-soft">
                                    {Object.entries(TICKET_PRIORITY_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                </select>
                                <select value={creating.source} onChange={(e) => setCreating(c => ({ ...c, source: e.target.value }))} className="input-soft">
                                    <option value="offline">Teren / Offline</option>
                                    <option value="phone">Telefon</option>
                                    <option value="email">Email</option>
                                    <option value="site">Site</option>
                                </select>
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2">
                            <button onClick={() => setCreating(null)} className="btn-outline">Anulează</button>
                            <button onClick={submitCreate} className="btn-primary" data-testid="tcreate-submit">Creează ticket</button>
                        </div>
                    </div>
                </div>
            )}

            {/* View ticket modal */}
            {viewing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-start justify-between">
                            <div>
                                <h3 className="font-heading text-xl">{viewing.subject}</h3>
                                <div className="text-xs text-muted-foreground mt-1">Ticket #{viewing.ticket_number} · {viewing.name || "—"} · {viewing.phone || "—"} {viewing.email && `· ${viewing.email}`}</div>
                                <div className="flex gap-2 mt-3">
                                    <select value={viewing.status} onChange={(e) => updateTicket({ status: e.target.value })} className="input-soft !py-1 text-xs" data-testid="tview-status">
                                        {Object.entries(TICKET_STATUS_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                    </select>
                                    <select value={viewing.priority || "normal"} onChange={(e) => updateTicket({ priority: e.target.value })} className="input-soft !py-1 text-xs">
                                        {Object.entries(TICKET_PRIORITY_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {isSuperAdmin && <button onClick={() => del(viewing.ticket_id)} className="btn-ghost text-destructive" title="Șterge ticket (superadmin)"><Trash2 className="h-4 w-4" /></button>}
                                <button onClick={() => setViewing(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                            </div>
                        </div>
                        <div className="p-5 space-y-5">
                            <div className="bg-muted p-3 rounded-xl text-sm"><div className="text-xs text-muted-foreground mb-1">Descriere</div>{viewing.description}</div>

                            {/* Assign installer */}
                            <div className="card-soft p-3">
                                <h4 className="font-medium mb-2 text-sm flex items-center gap-2"><UserCheck className="h-4 w-4" /> Atribuire tehnician</h4>
                                <select value={viewing.assigned_to || ""} onChange={(e) => assignInstaller(e.target.value)} className="input-soft" data-testid="tview-assign">
                                    <option value="">— Neatribuit —</option>
                                    {installers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                                </select>
                                {viewing.assigned_to_name && <div className="text-xs text-muted-foreground mt-1">Atribuit: {viewing.assigned_to_name}</div>}
                            </div>

                            {/* Service request accept */}
                            {viewing.source === "site_services" && !viewing.appointment_id && (
                                <div className="card-soft p-3 border-primary/40 bg-primary/5">
                                    <h4 className="font-medium mb-2 text-sm flex items-center gap-2"><CalendarPlus className="h-4 w-4 text-primary" /> Solicitare de serviciu</h4>
                                    {!acceptForm ? (
                                        <>
                                            <p className="text-sm text-muted-foreground mb-2">Acest ticket a fost deschis din pagina de Servicii. Acceptă solicitarea pentru a crea o programare.</p>
                                            <button onClick={() => setAcceptForm({ date: "", time_slot: "", installer_id: viewing.assigned_to || "", notes: "" })} className="btn-primary text-sm" data-testid="tview-accept-btn">Acceptă și creează programare</button>
                                        </>
                                    ) : (
                                        <div className="space-y-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                <input type="date" value={acceptForm.date} onChange={(e) => setAcceptForm({ ...acceptForm, date: e.target.value })} className="input-soft !py-1 text-sm" data-testid="tview-accept-date" />
                                                <input value={acceptForm.time_slot} onChange={(e) => setAcceptForm({ ...acceptForm, time_slot: e.target.value })} placeholder="Interval (10:00-14:00)" className="input-soft !py-1 text-sm" />
                                            </div>
                                            <select value={acceptForm.installer_id} onChange={(e) => setAcceptForm({ ...acceptForm, installer_id: e.target.value })} className="input-soft !py-1 text-sm">
                                                <option value="">Fără instalator (atribui mai târziu)</option>
                                                {installers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                                            </select>
                                            <textarea rows="2" value={acceptForm.notes} onChange={(e) => setAcceptForm({ ...acceptForm, notes: e.target.value })} placeholder="Observații pentru programare" className="input-soft text-sm" />
                                            <div className="flex gap-2">
                                                <button onClick={acceptRequest} className="btn-primary text-sm" data-testid="tview-accept-submit">Creează programare</button>
                                                <button onClick={() => setAcceptForm(null)} className="btn-outline text-sm">Renunță</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {viewing.appointment_id && (
                                <div className="text-xs text-muted-foreground">Legat de programarea <span className="font-mono">{viewing.appointment_id.slice(-8)}</span></div>
                            )}

                            {/* Resolution */}
                            <div className="card-soft p-3 bg-primary/5 border-primary/30">
                                <h4 className="font-medium mb-2 text-sm flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> Rezoluție</h4>
                                {viewing.resolution_at && viewing.status === "resolved" ? (
                                    <div>
                                        <div className="text-xs text-muted-foreground mb-1">Rezolvat la {new Date(viewing.resolution_at).toLocaleString("ro-RO")} de {viewing.resolved_by_name || "staff"}</div>
                                        <div className="bg-card rounded-xl p-3 text-sm whitespace-pre-line">{viewing.resolution}</div>
                                        <button onClick={() => updateTicket({ status: "in_progress" })} className="btn-outline text-xs mt-2">Redeschide ticket</button>
                                    </div>
                                ) : (
                                    <>
                                        <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows="4" placeholder="Descrie ce s-a făcut pentru a rezolva problema..." className="input-soft text-sm" data-testid="tview-resolution" />
                                        <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
                                            <input type="checkbox" checked={sendResEmail} onChange={(e) => setSendResEmail(e.target.checked)} data-testid="tview-send-email" disabled={!viewing.email} />
                                            Trimite rezoluția și clientului pe email {!viewing.email && <span className="text-xs text-muted-foreground">(fără email — indisponibil)</span>}
                                        </label>
                                        <button onClick={resolveTicket} className="btn-primary text-sm mt-2 flex items-center gap-2" data-testid="tview-resolve"><CheckCircle className="h-4 w-4" /> Marchează ca rezolvat</button>
                                    </>
                                )}
                            </div>

                            {/* Internal notes */}
                            <div>
                                <h4 className="font-medium mb-2 text-sm flex items-center gap-2"><StickyNote className="h-4 w-4" /> Mențiuni interne (doar staff)</h4>
                                <div className="space-y-2">
                                    {(viewing.internal_notes || []).map(n => (
                                        <div key={n.note_id} className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm" data-testid={`tnote-${n.note_id}`}>
                                            <div className="text-xs text-muted-foreground mb-1">{n.author_name} · {new Date(n.created_at).toLocaleString("ro-RO")}{n.edited_at && " · editat"}</div>
                                            {editingNote?.note_id === n.note_id ? (
                                                <>
                                                    <textarea value={editingNote.text} onChange={(e) => setEditingNote({ ...editingNote, text: e.target.value })} rows="2" className="input-soft" />
                                                    <div className="flex gap-2 mt-2">
                                                        <button onClick={saveEditNote} className="btn-primary !py-1 text-xs">Salvează</button>
                                                        <button onClick={() => setEditingNote(null)} className="btn-outline !py-1 text-xs">Anulează</button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="whitespace-pre-line flex-1">{n.text}</div>
                                                    <div className="flex gap-1">
                                                        <button onClick={() => setEditingNote({ note_id: n.note_id, text: n.text })} className="btn-ghost"><Edit2 className="h-3 w-3" /></button>
                                                        <button onClick={() => deleteNote(n.note_id)} className="btn-ghost text-destructive"><Trash2 className="h-3 w-3" /></button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Adaugă mențiune internă..." className="input-soft flex-1" data-testid="tnote-input" />
                                    <button onClick={addNote} className="btn-primary flex items-center gap-1"><Plus className="h-4 w-4" /></button>
                                </div>
                            </div>

                            {/* Interventions */}
                            <div>
                                <h4 className="font-medium mb-2 text-sm flex items-center gap-2"><Hammer className="h-4 w-4" /> Intervenții realizate</h4>
                                <div className="space-y-2">
                                    {(viewing.interventions || []).map(iv => (
                                        <div key={iv.intervention_id} className="card-soft p-3 text-sm" data-testid={`tintv-${iv.intervention_id}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <div className="font-medium">{iv.work_done}</div>
                                                    <div className="text-xs text-muted-foreground">{iv.date} · {iv.technician_name} · {iv.duration_minutes} min · {iv.cost.toFixed(2)} RON</div>
                                                    {(iv.parts && iv.parts.length > 0) ? (
                                        <div className="text-xs mt-1 space-y-0.5">
                                            <span className="text-muted-foreground font-medium">Piese:</span>
                                            {iv.parts.map((p, i) => (
                                                <div key={i} className="ml-2">
                                                    {p.product_name}
                                                    {p.product_code && <span className="text-muted-foreground ml-1">#{p.product_code}</span>}
                                                    {" "}× {p.qty}
                                                    {p.location_name && <span className="text-muted-foreground ml-1">({p.location_name})</span>}
                                                </div>
                                            ))}
                                        </div>
                                    ) : iv.parts_used ? (
                                        <div className="text-xs mt-1">Piese: {iv.parts_used}</div>
                                    ) : null}
                                                </div>
                                                <button onClick={() => deleteIntv(iv.intervention_id)} className="btn-ghost text-destructive"><Trash2 className="h-3 w-3" /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="card-soft p-3 mt-2 space-y-2">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                        <input type="date" value={ivForm.date} onChange={(e) => setIvForm({ ...ivForm, date: e.target.value })} className="input-soft !py-1 text-sm" />
                                        <input type="number" min="0" placeholder="Durată (min)" value={ivForm.duration_minutes} onChange={(e) => setIvForm({ ...ivForm, duration_minutes: Number(e.target.value) })} className="input-soft !py-1 text-sm" />
                                        <input type="number" min="0" step="0.01" placeholder="Cost (RON)" value={ivForm.cost} onChange={(e) => setIvForm({ ...ivForm, cost: Number(e.target.value) })} className="input-soft !py-1 text-sm" />
                                    </div>
                                    <textarea rows="2" placeholder="Lucrări efectuate" value={ivForm.work_done} onChange={(e) => setIvForm({ ...ivForm, work_done: e.target.value })} className="input-soft text-sm" data-testid="tintv-work" />
                                    <PartsPicker parts={ivParts} onChange={setIvParts} />
                                    <button onClick={addIntv} className="btn-primary text-sm" data-testid="tintv-add">Salvează intervenția</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
